import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {map, mergeMap} from 'rxjs/operators';
import {VehicleApiModel} from '../../../api/models/vehicle.api-model';
import {VehicleRestService} from '../../../api/services/vehicle.rest.service';
import {VehicleTypeRestService} from '../../../api/services/vehicle-type.rest.service';
import {QueryParamsApiModel} from '../../../api/models/query-params-api.model';
import {FilterParameterApiModel} from '../../../api/models/filter-parameter.api.model';
import {VehicleTypeApiModel} from '../../../api/models/vehicle-type.api-model';
import {VehicleViewModel} from './vehicle.view-model';
import {VehicleTypeViewModel} from '../../../core/models/vehicle-type.view-model';
import {resourceIri} from '../../../core/helpers/resource-reference.helper';

/** Hydrates only missing read data; it keeps no values between requests/viewers. */
@Injectable({providedIn: 'root'})
export class VehicleReadHydrator {
  constructor(private readonly vehicles: VehicleRestService, private readonly types: VehicleTypeRestService) {}

  public hydrate(relations: Array<string | VehicleApiModel | null | undefined>): Observable<VehicleViewModel[]> {
    const byIri = new Map<string, string | VehicleApiModel>();
    for (const relation of relations) {
      const iri = resourceIri(relation);
      if (iri && (!byIri.has(iri) || this.hasReadData(relation))) {
        byIri.set(iri, relation);
      }
    }
    const missing = [...byIri].filter(([, value]) => !this.hasReadData(value)).map(([iri]) => iri);
    const params = new QueryParamsApiModel(missing.map(iri => new FilterParameterApiModel('id', iri)));
    params.pagination = false;
    const fetched$ = missing.length
      ? this.vehicles.getVehicleCollection(params).pipe(map(result => result.records))
      : of<VehicleApiModel[]>([]);

    return fetched$.pipe(mergeMap(fetched => {
      const records = [...byIri].map(([iri, value]) => this.hasReadData(value)
        ? value : fetched.find(vehicle => vehicle['@id'] === iri)).filter((value): value is VehicleApiModel => !!value);
      const needsTypes = records.some(vehicle => !!vehicle.vehicleType &&
        (typeof vehicle.vehicleType === 'string' || !vehicle.vehicleType.code));
      const typeParams = new QueryParamsApiModel();
      typeParams.pagination = false;
      const types$ = needsTypes
        ? this.types.getVehicleTypeCollection(typeParams).pipe(map(result => result.records))
        : of<VehicleTypeApiModel[]>([]);
      return types$.pipe(map(types => records.map(vehicle => {
        const model = new VehicleViewModel(vehicle);
        if (!model.vehicleType?.code) {
          const type = types.find(candidate => candidate['@id'] === resourceIri(vehicle.vehicleType));
          if (type) {
            model.vehicleType = new VehicleTypeViewModel(type);
          }
        }
        return model;
      })));
    }));
  }

  private hasReadData(value: string | VehicleApiModel | null | undefined): value is VehicleApiModel {
    return !!value && typeof value !== 'string' && !!value.fullVehicleIdentity &&
      !!value.vehicleType && (typeof value.vehicleType === 'string' ||
        !!value.vehicleType.code || !!resourceIri(value.vehicleType));
  }
}
